/**
 * Paynow Payment Service
 * Handles integration with Paynow payment gateway
 * Reference: https://developers.paynow.co.zw/
 */

import config from '../config';
import { getToken } from './tokenStorage';

interface PaynowInitiateResponse {
  success: boolean;
  redirect?: string;
  reference?: string;
  pollUrl?: string;
  error?: string;
}

interface PaynowStatusCheckResponse {
  reference: string;
  paid: boolean;
  amount: number;
  status: 'pending' | 'paid' | 'failed' | 'cancelled';
  topUpStatus?: string;
  topUpError?: string;
  meterProvider?: string;
  meterId?: string;
  metaInfo?: unknown;
}


class PaynowService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.API_BASE_URL;
  }


  async initiatePayment(
    amount: number,
    currency: string,
    reference: string,
    email: string,
    phone: string,
    propertyId: string,
    purpose: string,
    returnUrl: string
  ): Promise<PaynowInitiateResponse> {
    try {
      const token = await getToken();
      const payload = {
        amount,
        currency,
        reference,
        email,
        phone,
        propertyId,
        purpose,
        returnUrl,
      };

      const response = await fetch(`${this.baseUrl}/payments/intents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const data = await response.json();
      console.log('data from initiatePayment:', data);


      const redirect = data.redirect || data.redirectUrl || data.redirect_url || data.url || data.link;
      const pollUrl = data.pollUrl || data.pollURL || data.poll_url || data.poll;
      const referenceValue = data.reference || data.ref || data.referenceId || reference;

      console.log('resolved paynow response values', { redirect, pollUrl, referenceValue });


      if (redirect) {
        return {
          success: true,
          redirect,
          reference: referenceValue,
          pollUrl,
        };
      }


      if (pollUrl) {
        return {
          success: true,
          redirect: pollUrl,
          reference: referenceValue,
          pollUrl,
        };
      }

      return {
        success: false,
        error: data.error || 'Failed to initiate payment',
      };
    } catch (error) {
      console.error('Error initiating Paynow payment:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }


  async checkPaymentStatus(reference: string): Promise<PaynowStatusCheckResponse | null> {
    try {
      const token = await getToken();
      const response = await fetch(`${this.baseUrl}/payments/paynow/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ reference }),
      });

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking Paynow payment status:', error);
      return null;
    }
  }
  /**
   * Format amount for Paynow (convert cents to main currency unit)
   */
  formatAmount(amountInCents: number): number {
    return amountInCents / 100;
  }

  /**
   * Format amount from main currency unit to cents
   */
  formatAmountToCents(amount: number): number {
    return Math.round(amount * 100);
  }
}

// Export singleton instance
export default new PaynowService();

// Export types
export type { PaynowInitiateResponse, PaynowStatusCheckResponse };
